import { Injectable } from '@angular/core';
import {
  BehaviorSubject, Observable, map, tap, take,
  withLatestFrom, of, finalize
} from 'rxjs';

import { IAPIFetchRequestOptions } from '@sondermind/utilities/models-http';

import { ProviderHttpService } from '../services/provider.httpservice';
import { Provider } from '../models/provider.model';

/**
 * Store for managing provider data
 */
@Injectable({
  providedIn: 'root'
})
export class ProviderStore {
  private providersSubject = new BehaviorSubject<Map<string, Provider>>(new Map());
  private isLoadingSubject = new BehaviorSubject<boolean>(false);

  /**
   * Observable of all cached providers as a Map (keyed by account UUID)
   */
  providers$ = this.providersSubject.asObservable();

  /**
   * Observable indicating if data is currently being loaded
   */
  isLoading$ = this.isLoadingSubject.asObservable();

  constructor(
    private providerHttpService: ProviderHttpService
  ) {}

  /**
   * Loads a specific provider by account UUID
   * @param accountUuid The account UUID of the provider to load
   * @param forceRefresh Whether to force refresh the data even if already cached
   * @param opts Optional request options
   * @returns Observable of Provider
   */
  loadProvider$(
    accountUuid: string,
    forceRefresh: boolean = false,
    opts: Partial<IAPIFetchRequestOptions> = {}
  ): Observable<Provider> {
    const cached = this.providersSubject.value.get(accountUuid);

    if (forceRefresh || !cached) {
      this.isLoadingSubject.next(true);

      return this.providerHttpService.fetchByAccountUuid({
        filterArray: [{ key: 'provider_account_ids', value: `${accountUuid}` }],
        ...opts
      }).pipe(
        withLatestFrom(this.providers$),
        map(([provider, currentProviders]) => {
          const updatedProviders = new Map(currentProviders);
          updatedProviders.set(accountUuid, provider);
          return updatedProviders;
        }),
        tap((updatedProviders) => {
          this.providersSubject.next(updatedProviders);
        }),
        map((providers) => providers.get(accountUuid) as Provider),
        finalize(() => this.isLoadingSubject.next(false))
      );
    }

    return this.providers$.pipe(
      map((providers) => providers.get(accountUuid) as Provider),
      take(1)
    );
  }

  /**
   * Loads a specific provider by persona provider ID
   * @param personaProviderId The persona provider ID of the provider to load
   * @returns Observable of Provider
   */
  loadProviderByPersonaProviderId$(personaProviderId: number): Observable<Provider> {
    this.isLoadingSubject.next(true);

    return this.providerHttpService.fetchByPersonaProviderIds$([personaProviderId]).pipe(
      withLatestFrom(this.providers$),
      map(([providers, currentProviders]) => {
        const updatedProviders = new Map(currentProviders);

        providers.forEach((provider) => {
          updatedProviders.set(provider.providerAccountId, provider);
        });

        this.providersSubject.next(updatedProviders);
        this.isLoadingSubject.next(false);

        if (providers.length > 0) {
          return providers[0];
        }

        throw new Error(`No provider found for persona_provider_id: ${personaProviderId}`);
      })
    );
  }

  /**
   * Loads multiple providers by account UUIDs
   * @param accountUuids Array of account UUIDs of the providers to load
   * @param forceRefresh Whether to force refresh the data even if already cached
   * @returns Observable of Provider array
   */
  loadProviders$(
    accountUuids: string[],
    forceRefresh: boolean = false
  ): Observable<Provider[]> {
    const currentProviders = this.providersSubject.value;
    const uncachedUuids: string[] = [];
    const cachedProviders: Provider[] = [];

    // Check which providers are already cached
    accountUuids.forEach((id) => {
      const cached = currentProviders.get(id);
      if (forceRefresh || !cached) {
        uncachedUuids.push(id);
      } else {
        cachedProviders.push(cached);
      }
    });

    // If all providers are cached and we're not forcing refresh, return cached data
    if (uncachedUuids.length === 0) {
      return of(cachedProviders);
    }

    // Load uncached providers
    this.isLoadingSubject.next(true);
    return this.providerHttpService.list$({
      filterArray: uncachedUuids.map((id) => ({ key: 'provider_account_ids', value: id }))
    }).pipe(
      withLatestFrom(this.providers$),
      map(([newProviders, currentProvidersMap]) => {
        const updatedProviders = new Map(currentProvidersMap);

        // Add newly loaded providers to the cache
        // Note: We need to map providers to their corresponding accountUuids
        newProviders.forEach((provider) => {
          updatedProviders.set(provider.providerAccountId, provider);
        });

        return updatedProviders;
      }),
      tap((updatedProviders) => {
        this.providersSubject.next(updatedProviders);
        this.isLoadingSubject.next(false);
      }),
      map((providers) => accountUuids
        .map((uuid) => providers.get(uuid)!)
        .filter((x) => !!x))
    );
  }

  /**
   * Gets a specific provider by account UUID from the cache
   * @param accountUuid The account UUID of the provider to find
   * @returns Observable of Provider or null if not found
   */
  getProviderByAccountUuid$(accountUuid: string): Observable<Provider | undefined> {
    return this.providers$.pipe(
      map((providers) => providers.get(accountUuid))
    );
  }

  /**
   * Clears all cached provider data
   */
  clearCache(): void {
    this.providersSubject.next(new Map());
  }

  /**
   * Removes a specific provider from the cache
   * @param accountUuid The account UUID of the provider to remove
   */
  removeProviderFromCache(accountUuid: string): void {
    const currentProviders = new Map(this.providersSubject.value);
    currentProviders.delete(accountUuid);
    this.providersSubject.next(currentProviders);
  }

  isClientSaasInvited$(): Observable<boolean> {
    return this.providers$.pipe(
      map((providers) => [...providers.values()].some((provider) => provider.saasEnabled))
    );
  }
}
